import { queryKeys } from '@constants/queryKeys';
import { useDeleteMasterItem, useMainCentreList } from '@features/settings';
import { useRealtimeInvalidate } from '@hooks/useRealtimeInvalidate';
import { AddOutlined, ApartmentOutlined, DeleteOutlined } from '@mui/icons-material';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import type { MainCentreItem } from '@services/master.service';
import { useState } from 'react';

import { AddMainCentreDialog } from './AddMainCentreDialog';

interface ManageMainCentresDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * "Manage Main Centres" — opened from the top-bar Main Centre switcher (admin
 * only). Lists every city with its shared GSTIN and a Delete action; the
 * header's "New Main Centre" button opens `AddMainCentreDialog` nested on top.
 * Mirrors ManageCentresDialog one level up.
 */
export const ManageMainCentresDialog = ({ open, onClose }: ManageMainCentresDialogProps) => {
  const { data, isLoading } = useMainCentreList();
  const deleteItem = useDeleteMasterItem('main-centres');
  const [newMainCentreOpen, setNewMainCentreOpen] = useState(false);
  const [pendingRemove, setPendingRemove] = useState<MainCentreItem | null>(null);

  // Live-refresh while the modal is open — the server broadcasts
  // `main-centres:changed` to every admin on each mutation.
  useRealtimeInvalidate('main-centres', [queryKeys.master.list('main-centres')]);

  const mainCentres = data ?? [];

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        slotProps={{ paper: { sx: { borderRadius: '16px', p: '8px', width: { xs: 'calc(100vw - 32px)', sm: 480 }, maxWidth: 'calc(100vw - 32px)' } } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', px: '20px', pt: '16px', pb: '4px' }}>
          <Typography sx={(theme) => ({ fontSize: 16, fontWeight: 700, color: theme.palette.grey[900] })}>
            Manage Main Centres
          </Typography>
          <Button
            onClick={() => setNewMainCentreOpen(true)}
            startIcon={<AddOutlined sx={{ fontSize: 16 }} />}
            sx={(theme) => ({
              bgcolor: theme.palette.primary.main, borderRadius: '8px', color: 'white', fontSize: 13, fontWeight: 600,
              textTransform: 'none', px: '14px', py: '7px', boxShadow: 'none', flexShrink: 0,
              '&:hover': { bgcolor: theme.palette.primary.dark, boxShadow: 'none' },
            })}
          >
            New Main Centre
          </Button>
        </Box>
        <DialogContent sx={{ minHeight: 100 }}>
          {isLoading ? (
            <Box sx={{ py: '24px', display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={20} />
            </Box>
          ) : mainCentres.length === 0 ? (
            <Typography sx={(theme) => ({ fontSize: 13.5, color: theme.palette.grey[500], textAlign: 'center', py: '24px' })}>
              No main centres yet — add one to get started.
            </Typography>
          ) : (
            mainCentres.map((m, idx) => (
              <Box
                key={m._id}
                sx={(theme) => ({
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
                  py: '12px', borderBottom: idx < mainCentres.length - 1 ? `1px solid ${theme.palette.grey[100]}` : 'none',
                })}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', minWidth: 0 }}>
                  <ApartmentOutlined sx={(theme) => ({ fontSize: 16, color: theme.palette.grey[400], flexShrink: 0 })} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={(theme) => ({ fontSize: 14, fontWeight: 600, color: theme.palette.grey[700], overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' })}>
                      {m.name}{m.code ? ` (${m.code})` : ''}
                    </Typography>
                    <Typography sx={(theme) => ({ fontSize: 11, color: theme.palette.grey[500], overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' })}>
                      {m.gstin ? `GSTIN ${m.gstin}` : 'No GSTIN set'}
                    </Typography>
                    {!m.active && (
                      <Typography sx={(theme) => ({ fontSize: 11, color: theme.palette.error.main, fontWeight: 600 })}>Inactive</Typography>
                    )}
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  disabled={!m.active}
                  onClick={() => setPendingRemove(m)}
                  sx={(theme) => ({
                    width: 30, height: 30, borderRadius: '7px', flexShrink: 0,
                    bgcolor: '#fff1f0', color: theme.palette.error.main,
                    '&:hover': { bgcolor: '#fee2e2' },
                    '&.Mui-disabled': { opacity: 0.35 },
                  })}
                >
                  <DeleteOutlined sx={{ fontSize: 17 }} />
                </IconButton>
              </Box>
            ))
          )}
        </DialogContent>
        <DialogActions sx={{ px: '20px', pb: '16px' }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={(theme) => ({
              borderRadius: '8px', color: theme.palette.grey[700], fontSize: 13, fontWeight: 600,
              textTransform: 'none', px: '18px', py: '9px', borderColor: theme.palette.grey[200],
              '&:hover': { bgcolor: theme.palette.grey[50], borderColor: theme.palette.grey[300] },
            })}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={!!pendingRemove}
        onClose={() => setPendingRemove(null)}
        slotProps={{ paper: { sx: { borderRadius: '14px', p: '8px', width: 360, maxWidth: 'calc(100vw - 32px)' } } }}
      >
        <DialogTitle sx={(theme) => ({ fontSize: 15, fontWeight: 700, color: theme.palette.grey[900], pb: '6px' })}>
          Remove "{pendingRemove?.name}"?
        </DialogTitle>
        <DialogContent>
          <Typography sx={(theme) => ({ fontSize: 13.5, color: theme.palette.grey[500] })}>
            This will deactivate the main centre. Its centres stay on record, but no one will be able
            to select this main centre — including on the login screen.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: '20px', pb: '16px', gap: '8px' }}>
          <Button
            onClick={() => setPendingRemove(null)}
            sx={(theme) => ({
              fontSize: 13, fontWeight: 600, textTransform: 'none', color: theme.palette.grey[700], borderRadius: '8px',
              '&:hover': { bgcolor: theme.palette.grey[100] },
            })}
          >
            Cancel
          </Button>
          <Button
            onClick={() => { if (pendingRemove) deleteItem.mutate(pendingRemove._id); setPendingRemove(null); }}
            variant="contained"
            sx={(theme) => ({
              fontSize: 13, fontWeight: 600, textTransform: 'none',
              bgcolor: theme.palette.error.main, color: 'white', borderRadius: '8px', boxShadow: 'none',
              '&:hover': { bgcolor: theme.palette.status.error.text, boxShadow: 'none' },
            })}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      <AddMainCentreDialog open={newMainCentreOpen} onClose={() => setNewMainCentreOpen(false)} />
    </>
  );
};
